import type { WebVitalReport } from '~/core/ports/datadog';
import type { Metric } from 'web-vitals';

/**
 * Where each metric's measured interval begins.
 *
 * TTFB, FCP and LCP measure from the navigation onwards, so their interval
 * starts at the navigation. INP measures one interaction, which can happen at
 * any point in the page's life, so its interval starts at that interaction.
 * CLS never becomes a duration vital, and only needs an instant to be filed
 * under the right view.
 */
const ANCHOR = {
	CLS: 'navigation',
	FCP: 'navigation',
	LCP: 'navigation',
	TTFB: 'navigation',
	INP: 'entry'
} as const satisfies Record<Metric['name'], 'navigation' | 'entry'>;

/** Milliseconds between the time origin and the start of the measured interval. */
function relativeStart(metric: Metric): number {
	const navigationStart = metric.navigationStartTime ?? 0;

	if (ANCHOR[metric.name] === 'navigation') return navigationStart;

	const entryStarts = metric.entries.map(entry => entry.startTime);

	return entryStarts.length ? Math.min(...entryStarts) : navigationStart;
}

function pathnameOf(url: string): string {
	try {
		return new URL(url).pathname;
	} catch {
		return url;
	}
}

/**
 * Freezes the temporal and page data web-vitals reports, so neither depends on
 * when the metric reaches Datadog. The callback runs on an idle tick and the
 * send waits for the SDK, and a route change in between would otherwise file
 * the metric under the destination page.
 */
export function toWebVitalReport(
	metric: Metric,
	currentUrl: string
): WebVitalReport {
	const start = relativeStart(metric);
	const timeOrigin =
		typeof performance !== 'undefined' && performance.timeOrigin
			? performance.timeOrigin
			: Date.now() - start - metric.value;
	const url = metric.navigationURL ?? currentUrl;

	return {
		name: metric.name,
		value: metric.value,
		rating: metric.rating,
		startedAt: timeOrigin + start,
		url,
		path: pathnameOf(url)
	};
}
